import React, { useState, useEffect } from 'react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { useLanguage } from '../context/LanguageContext';
import { t } from '../translations/translations';
import { initScrollAnimations, cleanupScrollAnimations } from '../utils/scrollAnimations';
import '../styles/contact.css';
import '../styles/animations.css';

const Contact = () => {
  const { language } = useLanguage();
  const [isScrolled, setIsScrolled] = useState(false);
  const [emailHasContent, setEmailHasContent] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    eventType: '',
    eventDate: '',
    guestCount: '',
    message: '',
    // Additional state for the new form
    name2: '',
    email2: '',
    phone2: '',
    eventType2: '',
    eventDate2: '',
    guestCount2: '',
    message2: ''
  });
  const [formStatus, setFormStatus] = useState(''); // 'loading', 'success', 'error'
  const [formMessage, setFormMessage] = useState('');

  useEffect(() => {
    const handleScroll = () => {
      const scrollPosition = window.scrollY;
      setIsScrolled(scrollPosition > 200);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Initialize scroll animations
  useEffect(() => {
    const scrollObserver = initScrollAnimations();
    return () => {
      cleanupScrollAnimations(scrollObserver);
    };
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    
    // Special handling for phone number formatting
    if (name === 'phone2') {
      // Remove all non-numeric characters
      const numericValue = value.replace(/\D/g, '');
      
      // Limit to 9 digits
      const limitedValue = numericValue.slice(0, 9);
      
      // Format as ###-###-###
      let formattedValue = '';
      if (limitedValue.length > 0) {
        if (limitedValue.length <= 3) {
          formattedValue = limitedValue;
        } else if (limitedValue.length <= 6) {
          formattedValue = limitedValue.slice(0, 3) + '-' + limitedValue.slice(3);
        } else {
          formattedValue = limitedValue.slice(0, 3) + '-' + limitedValue.slice(3, 6) + '-' + limitedValue.slice(6);
        }
      }
      
      setFormData(prev => ({
        ...prev,
        [name]: formattedValue
      }));
    } else if (name === 'eventDate2') {
      // Special handling for date formatting - more flexible approach
      // Remove all non-numeric characters
      const numericValue = value.replace(/\D/g, '');
      
      // Limit to 8 digits (DDMMYYYY)
      const limitedValue = numericValue.slice(0, 8);
      
      // Format as DD/MM/YYYY with validation
      let formattedValue = '';
      if (limitedValue.length > 0) {
        if (limitedValue.length <= 2) {
          // First two digits - day (01-31)
          const day = parseInt(limitedValue);
          if (day > 31) {
            formattedValue = '31';
          } else {
            formattedValue = limitedValue;
          }
        } else if (limitedValue.length <= 4) {
          // First four digits - day/month (DD/MM)
          const day = parseInt(limitedValue.slice(0, 2));
          const month = parseInt(limitedValue.slice(2, 4));
          
          let validDay = day;
          let validMonth = month;
          
          // Allow partial input - don't force validation until complete
          if (limitedValue.length === 3) {
            // Only 3 digits - just format what we have
            formattedValue = limitedValue.slice(0, 2) + '/' + limitedValue.slice(2);
          } else {
            // 4 digits - validate
            if (day > 31) validDay = 31;
            if (month > 12) validMonth = 12;
            
            formattedValue = validDay.toString().padStart(2, '0') + '/' + validMonth.toString().padStart(2, '0');
          }
        } else {
          // All eight digits - DD/MM/YYYY
          const day = parseInt(limitedValue.slice(0, 2));
          const month = parseInt(limitedValue.slice(2, 4));
          const year = limitedValue.slice(4, 8);
          
          let validDay = day;
          let validMonth = month;
          
          if (day > 31) validDay = 31;
          if (month > 12) validMonth = 12;
          
          formattedValue = validDay.toString().padStart(2, '0') + '/' + validMonth.toString().padStart(2, '0') + '/' + year;
        }
      }
      
      setFormData(prev => ({
        ...prev,
        [name]: formattedValue
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
      
      // Track email content for label behavior
      if (name === 'email2') {
        setEmailHasContent(value.length > 0);
      }
    }
  };

  const handleKeyDown = (e) => {
    // Only allow numeric keys, backspace, delete, tab, and arrow keys for phone and date inputs
    if (e.target.name === 'phone2' || e.target.name === 'eventDate2') {
      const allowedKeys = ['Backspace', 'Delete', 'Tab', 'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown'];
      const isNumeric = e.key >= '0' && e.key <= '9';
      
      if (!allowedKeys.includes(e.key) && !isNumeric) {
        e.preventDefault();
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Split name into firstName and lastName
    const nameParts = formData.name2.trim().split(' ');
    const firstName = nameParts[0] || '';
    const lastName = nameParts.slice(1).join(' ') || nameParts[0] || '';

    // Validate required fields
    if (!formData.name2 || !formData.email2 || !formData.message2) {
      setFormStatus('error');
      setFormMessage(language === 'pl' ? 'Proszę wypełnić wszystkie wymagane pola.' : 'Please fill in all required fields.');
      return;
    }

    setFormStatus('loading');
    setFormMessage('');

    try {
      const response = await fetch('/api/send-contact', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          firstName: firstName,
          lastName: lastName,
          email: formData.email2,
          phone: formData.phone2,
          eventType: formData.eventType2,
          eventDate: formData.eventDate2,
          guestCount: formData.guestCount2,
          message: formData.message2,
          language: language
        }),
      });

      const data = await response.json();

      if (response.ok) {
        setFormStatus('success');
        setFormMessage(data.message);
        // Clear form
        setFormData({
          ...formData,
          name2: '',
          email2: '',
          phone2: '',
          eventType2: '',
          message2: ''
        });
        // Reset status after 5 seconds
        setTimeout(() => {
          setFormStatus('');
          setFormMessage('');
        }, 5000);
      } else {
        setFormStatus('error');
        setFormMessage(data.error || (language === 'pl' ? 'Coś poszło nie tak. Spróbuj ponownie.' : 'Something went wrong. Please try again.'));
      }
    } catch (error) {
      console.error('Contact form error:', error);
      setFormStatus('error');
      setFormMessage(language === 'pl' ? 'Błąd połączenia. Spróbuj ponownie później.' : 'Connection error. Please try again later.');
    }
  };

  return (
    <div className="contact-page">
      <Header isScrolled={isScrolled} />
      
      {/* Hero Section */}
      <section 
        className="contact-hero"
        style={{
          background: 'linear-gradient(rgba(21, 21, 20, 0.4), rgba(21, 21, 20, 0.4)), url(/assets/Garbary22.jpg)',
          backgroundSize: '170%',
          backgroundPosition: 'top left'
        }}
      >
        <div className="contact-hero-content">
        <h1 className="contact-hero-title">{t('contact.hero.title', language)}</h1>
        <p className="contact-hero-subtitle">{t('contact.hero.subtitle', language)}</p>
        </div>
      </section>

      {/* Contact Details & Form Section */}
      <section className="contact-details-form scroll-animate">
        <div className="contact-details-form-content">
          <div className="contact-details-form-left scroll-animate delay-100">
            <div className="contact-details-form-info">
              <h2 className="contact-details-form-title">
                <span className="contact-title-get">{language === 'pl' ? 'Skontaktuj' : 'Get in'}</span>
                <span className="contact-title-touch">{language === 'pl' ? 'się' : 'touch'}</span>
              </h2>
              <div className="contact-details-form-item">
                <p>532 883 067</p>
                <p>Garbary96@gmail.com</p>
              </div>
              <div className="contact-details-form-spacing"></div>
              <div className="contact-details-form-item">
                <h4>Pawilon Krzyżanowskiego</h4>
                <p>Ul. Garbary 96<br />61-757 Poznań, Poland</p>
              </div>
            </div>
          </div>
          <div className="contact-details-form-right">
            <form className="contact-details-form-form" onSubmit={handleSubmit}>
              <div className="form-row">
                <div className="form-group form-group-name">
                  <input
                    type="text"
                    id="name2"
                    name="name2"
                    value={formData.name2}
                    onChange={handleInputChange}
                    required
                  />
                  <label htmlFor="name2">{language === 'pl' ? 'Imię i Nazwisko' : 'Full Name'}</label>
                </div>
                <div className={`form-group form-group-email ${emailHasContent ? 'email-has-content' : ''}`}>
                  <input
                    type="email"
                    id="email2"
                    name="email2"
                    value={formData.email2}
                    onChange={handleInputChange}
                    required
                  />
                  <label htmlFor="email2">{language === 'pl' ? 'Adres Email' : 'Email Address'}</label>
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <input
                    type="tel"
                    id="phone2"
                    name="phone2"
                    value={formData.phone2}
                    onChange={handleInputChange}
                    onKeyDown={handleKeyDown}
                    required
                  />
                  <label htmlFor="phone2">{language === 'pl' ? 'Numer Telefonu' : 'Phone Number'}</label>
                </div>
                <div className="form-group form-group-event-type">
                  <select
                    id="eventType2"
                    name="eventType2"
                    value={formData.eventType2}
                    onChange={handleInputChange}
                    required
                  >
                    <option value="">{language === 'pl' ? 'Typ Wydarzenia' : 'Event Type'}</option>
                    <option value="wedding">{language === 'pl' ? 'Przyjęcie Weselne' : 'Wedding Reception'}</option>
                    <option value="corporate">{language === 'pl' ? 'Wydarzenie Korporacyjne' : 'Corporate Event'}</option>
                    <option value="birthday">{language === 'pl' ? 'Przyjęcie Urodzinowe' : 'Birthday Party'}</option>
                    <option value="anniversary">{language === 'pl' ? 'Rocznica' : 'Anniversary'}</option>
                    <option value="conference">{language === 'pl' ? 'Konferencja/Seminarium' : 'Conference/Seminar'}</option>
                    <option value="exhibition">{language === 'pl' ? 'Wystawa Sztuki' : 'Art Exhibition'}</option>
                    <option value="other">{language === 'pl' ? 'Inne' : 'Other'}</option>
                  </select>
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <input
                    type="text"
                    id="eventDate2"
                    name="eventDate2"
                    value={formData.eventDate2}
                    onChange={handleInputChange}
                    onKeyDown={handleKeyDown}
                    required
                  />
                  <label htmlFor="eventDate2">{language === 'pl' ? 'Preferowana Data' : 'Preferred Date'}</label>
                </div>
                <div className="form-group form-group-guests">
                  <select
                    id="guestCount2"
                    name="guestCount2"
                    value={formData.guestCount2}
                    onChange={handleInputChange}
                    required
                  >
                    <option value="">{language === 'pl' ? 'Liczba Gości' : 'Number of Guests'}</option>
                    <option value="10-20">{language === 'pl' ? '10-20 gości' : '10-20 guests'}</option>
                    <option value="21-40">{language === 'pl' ? '21-40 gości' : '21-40 guests'}</option>
                    <option value="41-60">{language === 'pl' ? '41-60 gości' : '41-60 guests'}</option>
                    <option value="60+">{language === 'pl' ? '60+ gości' : '60+ guests'}</option>
                  </select>
                </div>
              </div>
              <div className="form-group form-group-message form-group-full">
                <textarea
                  id="message2"
                  name="message2"
                  value={formData.message2}
                  onChange={handleInputChange}
                  rows="4"
                  maxLength="300"
                  required
                />
                <label htmlFor="message2">{language === 'pl' ? 'Opowiedz nam o swoim wydarzeniu' : 'Tell us about your event'}</label>
                <div className="char-counter">{formData.message2.length}/300</div>
              </div>
              <button 
                type="submit" 
                className="contact-details-form-button"
                disabled={formStatus === 'loading'}
              >
                {formStatus === 'loading' 
                  ? (language === 'pl' ? 'Wysyłanie...' : 'Sending...') 
                  : (language === 'pl' ? 'Wyślij Wiadomość' : 'Send Message')
                }
              </button>
              {formMessage && (
                <div className={`contact-form-message ${formStatus}`}>
                  {formMessage}
                </div>
              )}
            </form>
          </div>
        </div>
      </section>


      {/* Map Section */}
      <section className="contact-map-section scroll-animate">
        <div className="contact-map-content">
          <div className="contact-map scroll-animate delay-200">
            <iframe
              src="https://www.openstreetmap.org/export/embed.html?bbox=16.9387%2C52.4118%2C16.9391%2C52.4124&layer=mapnik&marker=52.41210,16.93890"
              width="100%"
              height="100%"
              style={{ border: 0, filter: 'grayscale(100%) brightness(1)' }}
              allowFullScreen
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              title="Kantor Krzyżanowskiego - ul. Garbary 96, Poznań"
            ></iframe>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Contact;
